import { sortRoomsByPriority } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { useEffect, useState, useCallback } from 'react';
import { useInterval } from 'hooks/usePolling';
import { commaSeparatedNumbers } from '../../utils/commaSeparatedNumbers';
import { getJitteredInterval } from 'utils/pollingUtility';
import {toast} from 'react-toastify';

export const useDefaultDetailedSchedule = () => {
  const { DetailedScheduleStore } = useStore();
  const [expandedRooms, setExpandedRooms] = useState<Record<number, boolean>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const POLLING_DELAY = getJitteredInterval();
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Fetch detailed schedule data
  const fetchDetailedSchedule = useCallback(
    async (isPollingRefresh = false) => {
      try {
        const today = new Date().toISOString().split('T')[0];
        await DetailedScheduleStore.fetchDetailedSchedule(today);
      } catch (error) {
        if (environment === 'development') {
          console.error('Error fetching detailed schedule:', error);
        }
        toast.error('Error fetching detailed schedule');
      } finally {
        // Mark initial load as complete after first data fetch
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      }
    },
    [DetailedScheduleStore, isInitialLoad]
  );

  // Ensure API is called on mount
  useEffect(() => {
    fetchDetailedSchedule(false); // Initial load
  }, [fetchDetailedSchedule]);

  // Polling functionality - refresh data every 10 seconds
  useInterval(() => {
    if (!DetailedScheduleStore.isLoading) {
      fetchDetailedSchedule(true); // Pass true to indicate this is a polling refresh
    }
  }, POLLING_DELAY);

  // Set all rooms to expanded when data is loaded
  useEffect(() => {
    if (
      DetailedScheduleStore.detailedSchedule &&
      DetailedScheduleStore.detailedSchedule.length > 0
    ) {
      // Create an object with all rooms set to true
      const allExpanded: Record<number, boolean> = {};
      DetailedScheduleStore.detailedSchedule.forEach(room => {
        allExpanded[room.room_id] = true;
      });
      setExpandedRooms(allExpanded);
    }
  }, [DetailedScheduleStore.detailedSchedule]);

  // Apply room sorting to detailed schedule data
  const sortedDetailedSchedule = DetailedScheduleStore.detailedSchedule
    ? sortRoomsByPriority(DetailedScheduleStore.detailedSchedule)
    : [];

  // Toggle room expansion
  const toggleRoom = (roomId: number) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  const handlePrint = () => {
    // Create a print-window directly
    const printWindow = window.open('', '', 'width=1200,height=900');
    if (!printWindow) {
      return;
    }

    // Get current date and time
    const currentDateTime = new Date().toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Helper function to extract clean level (L1, L2, L3) from job_id
    const formatJobId = (jobId: string, ndc: string) => {
      if (!jobId) return '';
      // If no NDC or NDC is empty/dash, it's a clean job - extract only L1, L2, or L3
      if (!ndc || ndc === '-' || ndc.trim() === '') {
        const match = jobId.match(/_(L\d)_/);
        return match ? match[1] : jobId;
      }
      return jobId;
    };

    // Helper function to format start time (MM/DD HH:MM AM/PM)
    const formatStartTime = (dateTimeStr: string) => {
      if (!dateTimeStr) return '';
      try {
        const date = new Date(dateTimeStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${month}/${day} ${displayHours}:${minutes} ${ampm}`;
      } catch (e) {
        return dateTimeStr;
      }
    };

    // Helper function to remove last 2 words from product name
    const formatProductName = (productName: string) => {
      if (!productName) return '';
      const words = productName.trim().split(/\s+/);
      if (words.length <= 2) return productName;
      return words.slice(0, -2).join(' ');
    };

    // Build HTML table directly from the data - use sorted data for printing
    let tableHTML = '';
    sortedDetailedSchedule.forEach(room => {
      tableHTML += `
            <div style="margin-bottom: 0.5rem;">
                <h3 style="padding: 0.2rem 0.3rem; margin: 0; font-size: 11px; font-weight: bold;">
                    ${room.name}
                </h3>
                <table style="width: 100%; border-collapse: collapse;">
                    <thead>
                        <tr>
                            <th class="j1">Job/Clean</th>
                            <th class="j1">Prty</th>
                            <th class="j1">NDC</th>
                            <th class="j1">Status</th>
                            <th class="j1">Product Name</th>
                            <th class="j1">Bottle Qty</th>
                            <th class="j1">Qty to Make</th>
                            <th class="j1">Run Type</th>
                            <th class="j1">Start Time</th>
                            <th class="j1">Dur.</th>
                            <th class="j1">Bulk Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${room.data
          .map(
            row => `
                            <tr>
                                <td>${formatJobId(row.job_id, row.ndc)}</td>
                                <td>${row.priority || ''}</td>
                                <td>${row.ndc || ''}</td>
                                <td>${row.schedule_status || ''}</td>
                                <td>${formatProductName(row.product_name)}</td>
                                <td>${row.bottle_qty ? commaSeparatedNumbers(row.bottle_qty) : ''}</td>
                                <td>${row.qty_to_make ? commaSeparatedNumbers(row.qty_to_make) : ''}</td>
                                <td>${row.run_type || ''}</td>
                                <td>${formatStartTime(row.start_time)}</td>
                                <td>${row.duration || ''}</td>
                                <td>${row.bulk_qty ? commaSeparatedNumbers(row.bulk_qty) : ''}</td>
                            </tr>
                        `
          )
          .join('')}
                    </tbody>
                </table>
            </div>
        `;
    });

    // Write HTML to print window
    printWindow.document.write(`
        <html>
            <head>
                <title>Print Detailed Schedule</title>
                <style>
                    body { 
                        background: white; 
                        margin: 0; 
                        padding: 5px; 
                        font-family: Arial, sans-serif;
                        font-size: 9px;
                    }
                    table {
                        table-layout: fixed;
                        width: 100%;
                    }
                    .header-container {
                        position: relative;
                        margin-bottom: 5px;
                        padding: 0;
                    }
                    .header {
                        font-size: 14px;
                        font-weight: bold;
                        margin: 0;
                        padding: 0;
                        text-align: center;
                    }
                    .datetime {
                        font-size: 9px;
                        color: #666;
                        margin: 0;
                        padding: 0;
                        position: absolute;
                        right: 0;
                        top: 0;
                    }
                    /* Column widths - optimized for 2-page printing */
                    th.j1:nth-of-type(1) { width: 8%; }  /* Job/Clean - reduced since showing only L1/L2/L3 */
                    th.j1:nth-of-type(2) { width: 4%; padding-right: 2px; }  /* Prty - reduced padding */
                    th.j1:nth-of-type(3) { width: 10%; }  /* NDC */
                    th.j1:nth-of-type(4) { width: 7%; }  /* Status */
                    th.j1:nth-of-type(5) { 
                        width: 18%; 
                    }  /* Product Name - last 2 words removed */
                    th.j1:nth-of-type(6) { width: 8%; }  /* Qty to Make */
                    th.j1:nth-of-type(7) { width: 7%; padding-right: 2px; }  /* Bottle Qty - reduced padding */
                    th.j1:nth-of-type(8) { width: 7%; }  /* Run Type */
                    th.j1:nth-of-type(9) { width: 12%; }  /* Start Time - widened for new format */
                    th.j1:nth-of-type(10) { width: 5%; padding: 2px; }  /* Dur. - reduced padding */
                    th.j1:nth-of-type(11) { width: 8%; }  /* Bulk Qty */
                    
                    /* Apply same widths to td elements */
                    td:nth-of-type(2) { padding-right: 2px; }  /* Prty */
                    td:nth-of-type(7) { padding-right: 2px; }  /* Bottle Qty */
                    td:nth-of-type(10) { padding: 2px; }  /* Dur. */
                    
                    table { 
                        border-collapse: collapse; 
                        width: 100%;
                        margin-bottom: 5px;
                    }
                    th, td { 
                        border: 1px solid #000; 
                        padding: 2px 4px; 
                        text-align: left;
                        font-size: 8px;
                    }
                    th { 
                        background: white;
                        font-weight: bold;
                    }
                    h3 {
                        font-size: 10px;
                        margin: 0 0 2px 0;
                        padding: 2px 0;
                        font-weight: bold;
                    }
                </style>
            </head>
            <body>
                <div class="header-container">
                    <div class="header">Detailed Schedule</div>
                    <div class="datetime">Generated on: ${currentDateTime}</div>
                </div>
                ${tableHTML}
            </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return {
    isLoading: isInitialLoad && DetailedScheduleStore.isLoading,
    detailedSchedule: sortedDetailedSchedule, // Use sorted data
    expandedRooms,
    toggleRoom,
    handlePrint
  };
};
